import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { startOfDay, endOfDay, subDays, subYears, startOfMonth, startOfYear } from 'date-fns';
import DateRangeSelector from 'garaje/components/date-range-selector';
import EnvoyTwoUpCalendar from 'garaje/components/envoy-two-up-calendar';
import UiCard from 'garaje/components/ui/card';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import { type CalendarDate } from 'garaje/utils/date-fns-tz-utilities';

interface DateRangeWithCalendarSignature {
  Args: {
    showPresetSelector: boolean;
    selectedDateRangeWithDefault: string;
    startDateFormat: string;
    endDateFormat: string;
    didSelectDateRange: (startDate: CalendarDate, endDate: CalendarDate) => void;
    startDateWithDefault: CalendarDate;
    endDateWithDefault: CalendarDate;
    setStartDate: (date: CalendarDate) => void;
    setEndDate: (date: CalendarDate) => void;
    timezone: string;
    isSingleDay?: boolean;
  };
  Element: HTMLDivElement;
}

export default class DateRangeWithCalendarComponent extends Component<DateRangeWithCalendarSignature> {
  @tracked showCalendar = false;
  @tracked selectedDateRange = '';

  get selectedDateRangeWithDefault(): string {
    const { selectedDateRange } = this;

    if (selectedDateRange) return selectedDateRange;

    return 'Custom';
  }

  get dateRangeFilterOptions(): string[] {
    const { selectedDateRangeWithDefault } = this;
    const options = [
      'Today',
      'Yesterday',
      'Past 7 days',
      'Past 30 days',
      'Past 60 days',
      'Past 90 days',
      'Month-to-date',
      'Year-to-date',
      'Past year',
      'All time',
    ];
    if (!options.includes(selectedDateRangeWithDefault)) {
      options.push(selectedDateRangeWithDefault);
    }

    return options;
  }

  @action
  toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  @action
  onDateRangeSelect(option: string): void {
    const { didSelectDateRange, timezone } = this.args;
    let start = modifyDateInTimeZone(new Date(), timezone, startOfDay);
    let end = modifyDateInTimeZone(new Date(), timezone, endOfDay);

    switch (option) {
      case 'Yesterday':
        start = subDays(start, 1);
        end = subDays(end, 1);
        break;
      case 'Past 7 days':
        start = subDays(start, 7);
        break;
      case 'Past 30 days':
        start = subDays(start, 30);
        break;
      case 'Past 60 days':
        start = subDays(start, 60);
        break;
      case 'Past 90 days':
        start = subDays(start, 90);
        break;
      case 'Month-to-date':
        start = startOfMonth(start);
        break;
      case 'Year-to-date':
        start = startOfYear(start);
        break;
      case 'Past year':
        start = subYears(start, 1);
        break;
      case 'All time':
        start = startOfDay(new Date('2000-01-01'));
        break;
      case 'Custom':
        return;
    }

    this.selectedDateRange = option;
    this.args.setStartDate(start);
    this.args.setEndDate(end);
    didSelectDateRange(start, end);
  }

  @action
  didSelectDateRange(startDate: Date, endDate: Date): void {
    this.args.didSelectDateRange(startDate, endDate);
  }

  @action
  setStartDate(date: Date): void {
    this.args.setStartDate(date);
  }

  @action
  setEndDate(date: Date): void {
    this.args.setEndDate(date);
  }

  @action
  onShowCalendar(): void {
    this.showCalendar = true;
  }

  @action
  onBlurCalendar(): void {
    this.showCalendar = false;
  }

  <template>
    <div class="pb-4 flex items-center" data-test-date-range-with-calendar>
      <div class="text-sm flex-auto min-w-0 min-h-0 flex">
        <div class="mx-2">
          {{#if @showPresetSelector}}
            <DateRangeSelector
              @onDateRangeSelect={{this.onDateRangeSelect}}
              @dateRangeFilterOptions={{this.dateRangeFilterOptions}}
              @selectedDateRange={{this.selectedDateRangeWithDefault}}
              @dropdownClass="!w-44"
              @triggerClass="!mb-0 !w-44 date-range-selector"
              data-test-date-range-options-menu
            />
          {{/if}}
        </div>
        <div class="relative outline-none" tabindex="0" {{on "blur" this.onBlurCalendar}}>
          <UiCard
            class="flex-row text-base cursor-pointer border-solid border-carbon-20 px-4 py-2"
            {{on "click" this.onShowCalendar}}
            data-test-date-range-trigger
          >
            <div class="mr-4" data-test-start-date>{{@startDateFormat}}</div>
            {{#unless @isSingleDay}}
              &#x2192;
              <div class="ml-4" data-test-end-date>{{@endDateFormat}}</div>
            {{/unless}}
          </UiCard>
          {{#if this.showCalendar}}
            <div class="absolute">
              <EnvoyTwoUpCalendar
                @noShadow={{false}}
                @didSelectDateRange={{@didSelectDateRange}}
                @startDate={{@startDateWithDefault}}
                @endDate={{@endDateWithDefault}}
                @setStartDate={{@setStartDate}}
                @setEndDate={{@setEndDate}}
              />
            </div>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
